import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import {
  Microscope,
  ArrowRight,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  Target,
  FileText,
  Users,
  Loader2,
  Copy,
  Check,
  RefreshCw,
  Sparkles,
  Zap,
  Info
} from 'lucide-react';

// Issue from AI analysis
interface PromptIssue {
  type: string;
  severity: 'critical' | 'warning' | 'suggestion';
  title: string;
  description: string;
  fix: string;
}

// Analysis result from API
interface AnalysisResult {
  summary: string;
  overall_assessment: string;
  scores: {
    clarity: number;
    specificity: number;
    context: number;
    structure: number;
    overall: number;
  };
  issues: PromptIssue[];
  strengths: string[];
  improved_prompt: string;
  tips: string[];
}

function generateSessionId() {
  return 'autopsy_' + Math.random().toString(36).substring(2, 15);
}

type Phase = 'intro' | 'input' | 'analyzing' | 'results';

// Issue type icons
const ISSUE_ICONS: Record<string, typeof AlertCircle> = {
  vague_request: Target,
  missing_context: Users,
  no_format_spec: FileText,
  ambiguous_scope: AlertTriangle,
  missing_constraints: AlertCircle,
  unclear_audience: Users,
  no_examples: Lightbulb,
  too_broad: AlertTriangle
};

export default function PromptAutopsy() {
  const [phase, setPhase] = useState<Phase>('intro');
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId] = useState(generateSessionId);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fetchApi } = useApi();

  const source = searchParams.get('from') || 'direct';

  useEffect(() => {
    if (phase === 'input') {
      trackEvent('autopsy_started', { source });
    }
  }, [phase]);

  async function trackEvent(eventType: string, data: Record<string, unknown> = {}) {
    try {
      await fetchApi('/api/autopsy/event', {
        method: 'POST',
        body: JSON.stringify({ event_type: eventType, session_id: sessionId, ...data })
      });
    } catch (e) {
      console.error('Failed to track event:', e);
    }
  }

  async function handleAnalyze() {
    if (!prompt.trim()) return;
    if (prompt.length < 10) {
      setError('Prompt must be at least 10 characters');
      return;
    }

    setPhase('analyzing');
    setError(null);

    try {
      const response = await fetchApi<{
        success: boolean;
        autopsy_id: string;
        analysis: AnalysisResult;
      }>('/api/autopsy/analyze', {
        method: 'POST',
        body: JSON.stringify({
          prompt,
          session_id: sessionId,
          source
        })
      });

      setResult(response.analysis);
      setPhase('results');
    } catch (e) {
      console.error('Analysis failed:', e);
      setError('Analysis failed. Please try again.');
      setPhase('input');
    }
  }

  function handleCopyImproved() {
    if (!result) return;
    navigator.clipboard.writeText(result.improved_prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    trackEvent('autopsy_copied_improved');
  }

  function handleReset() {
    setPrompt('');
    setResult(null);
    setError(null);
    setPhase('input');
  }

  function handleCTA() {
    trackEvent('autopsy_cta_clicked', { destination: user ? 'dashboard' : 'register' });
    navigate(user ? '/dashboard' : '/register?from=autopsy');
  }

  function getScoreColor(score: number) {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  }

  function getScoreLabel(score: number) {
    if (score >= 80) return 'Excellent prompt';
    if (score >= 60) return 'Good, with room to improve';
    if (score >= 40) return 'Needs work';
    return 'Major improvements needed';
  }

  function getSeverityColor(severity: string) {
    switch (severity) {
      case 'critical': return 'border-red-500/50 bg-red-500/10';
      case 'warning': return 'border-orange-500/50 bg-orange-500/10';
      default: return 'border-cyan-500/50 bg-cyan-500/10';
    }
  }

  function getSeverityIconColor(severity: string) {
    switch (severity) {
      case 'critical': return 'text-red-400';
      case 'warning': return 'text-orange-400';
      default: return 'text-cyan-400';
    }
  }

  function getSeverityLabel(severity: string) {
    switch (severity) {
      case 'critical': return 'Critical';
      case 'warning': return 'Warning';
      default: return 'Suggestion';
    }
  }

  function getScoreBarColor(score: number) {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  }

  return (
    <div className="max-w-3xl mx-auto py-8">
      {/* Intro */}
      {phase === 'intro' && (
        <div className="text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-500/10 rounded-full text-cyan-400 text-sm mb-4">
            <Microscope className="w-4 h-4" />
            AI-Powered Analysis
          </div>
          <h1 className="text-4xl font-bold text-white">Prompt Autopsy</h1>
          <p className="text-gray-400 text-lg max-w-lg mx-auto">
            Paste any prompt you've written. Our AI will dissect what's working, what's missing, and give you an improved version.
          </p>

          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 text-left space-y-4">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              What we analyze:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: Target, text: 'Clarity of your request' },
                { icon: FileText, text: 'Specificity & details' },
                { icon: Users, text: 'Context & audience' },
                { icon: Zap, text: 'Structure & organization' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-gray-300">
                  <item.icon className="w-5 h-5 text-cyan-400" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 pt-2">
              Plus: specific issues, suggestions to fix them, and an improved prompt you can copy.
            </p>
          </div>

          <button
            onClick={() => setPhase('input')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-lg transition-colors text-lg"
          >
            Analyze a Prompt
            <ArrowRight className="w-5 h-5" />
          </button>

          <p className="text-sm text-gray-500">Free to use. No account required.</p>
        </div>
      )}

      {/* Input */}
      {phase === 'input' && (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">Paste Your Prompt</h2>
            <p className="text-gray-400">Enter any prompt you've written or are planning to use with an AI</p>
          </div>

          <div className="space-y-4">
            <textarea
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                setError(null);
              }}
              placeholder="Paste your prompt here...

Example: 'Write me a blog post about marketing'

Or something more complex like: 'I need help writing an email to my boss asking for a raise...'"
              className="w-full h-56 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 resize-none font-mono text-sm"
              autoFocus
            />

            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{prompt.split(/\s+/).filter(Boolean).length} words</span>
              <span className={prompt.length > 5000 ? 'text-red-400' : ''}>
                {prompt.length}/5000 characters
              </span>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <button
              onClick={handleAnalyze}
              disabled={!prompt.trim() || prompt.length > 5000}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
            >
              <Microscope className="w-5 h-5" />
              Analyze with AI
            </button>
          </div>

          <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-4">
            <p className="text-sm text-gray-500">
              <strong className="text-gray-400">Tip:</strong> Try a real prompt you've used with ChatGPT, Claude, or Gemini. The more specific your prompt, the more detailed feedback you'll get.
            </p>
          </div>
        </div>
      )}

      {/* Analyzing */}
      {phase === 'analyzing' && (
        <div className="text-center py-16 space-y-6">
          <div className="relative">
            <Loader2 className="w-16 h-16 animate-spin text-cyan-400 mx-auto" />
            <Sparkles className="w-6 h-6 text-cyan-300 absolute top-0 right-1/3 animate-pulse" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">AI is Analyzing Your Prompt</h2>
            <p className="text-gray-400">Checking for issues and generating suggestions...</p>
          </div>
        </div>
      )}

      {/* Results */}
      {phase === 'results' && result && (
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gray-700/50 rounded-full text-gray-300 text-sm mb-4">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Analysis Complete
            </div>
          </div>

          {/* Score Overview */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="text-center sm:text-left">
                <div className={`text-5xl font-bold ${getScoreColor(result.scores.overall)}`}>
                  {result.scores.overall}
                </div>
                <p className="text-gray-400 text-sm mt-1">{getScoreLabel(result.scores.overall)}</p>
              </div>
              <div className="flex-1 w-full space-y-3">
                {[
                  { label: 'Clarity', score: result.scores.clarity },
                  { label: 'Specificity', score: result.scores.specificity },
                  { label: 'Context', score: result.scores.context },
                  { label: 'Structure', score: result.scores.structure }
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <span className="text-sm text-gray-400 w-20">{item.label}</span>
                    <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getScoreBarColor(item.score)} transition-all`}
                        style={{ width: `${item.score}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-300 w-8">{item.score}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="mt-6 pt-4 border-t border-gray-700">
              <p className="text-gray-300">{result.overall_assessment}</p>
            </div>
          </div>

          {/* Original prompt */}
          <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4">
            <p className="text-sm text-gray-500 mb-2">Your prompt:</p>
            <p className="text-gray-300 font-mono text-sm whitespace-pre-wrap">{prompt}</p>
          </div>

          {/* Issues */}
          {result.issues.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-400" />
                Issues Found ({result.issues.length})
              </h3>
              {result.issues.map((issue, idx) => {
                const Icon = ISSUE_ICONS[issue.type] || AlertCircle;
                return (
                  <div
                    key={idx}
                    className={`border rounded-xl p-4 ${getSeverityColor(issue.severity)}`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${getSeverityIconColor(issue.severity)}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-white">{issue.title}</h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getSeverityColor(issue.severity)}`}>
                            {getSeverityLabel(issue.severity)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 mb-3">{issue.description}</p>
                        <div className="bg-gray-900/50 rounded-lg px-3 py-2">
                          <p className="text-sm text-cyan-300">
                            <Lightbulb className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                            <strong>Fix:</strong> {issue.fix}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Strengths */}
          {result.strengths.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                What's Working
              </h3>
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <ul className="space-y-2">
                  {result.strengths.map((strength, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-green-300">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Improved prompt */}
          {result.improved_prompt && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                Improved Version
              </h3>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
                <pre className="text-cyan-200 whitespace-pre-wrap text-sm font-mono leading-relaxed">
                  {result.improved_prompt}
                </pre>
                <button
                  onClick={handleCopyImproved}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded-lg transition-colors text-sm"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy improved prompt'}
                </button>
              </div>
            </div>
          )}

          {/* Tips */}
          {result.tips.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-400" />
                Pro Tips
              </h3>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <ul className="space-y-2">
                  {result.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-blue-300 text-sm">
                      <span className="text-blue-400 font-bold">{idx + 1}.</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              onClick={handleReset}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Analyze Another Prompt
            </button>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 rounded-xl p-6 text-center">
            <h3 className="text-xl font-semibold text-white mb-2">
              Writing better prompts is just the beginning
            </h3>
            <p className="text-gray-300 mb-4">
              Master critical thinking in the AI age. Learn to evaluate AI outputs, spot biases, and think more clearly.
            </p>
            <button
              onClick={handleCTA}
              className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-lg transition-colors"
            >
              {user ? 'Continue Learning' : 'Start Learning Free'}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
